/**
 * Historical Reports Error Boundary
 * 
 * Catches and handles React errors specifically in the historical reports section
 * with user-friendly fallback UI and error reporting
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class HistoricalReportsErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
            errorInfo: null,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({
            error,
            errorInfo,
        });

        // Log error for debugging
        console.error('Historical Reports Error Boundary caught an error:', error, errorInfo);

        // Call custom error handler if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    private handleRetry = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
    };

    private handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            // Use custom fallback if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default error UI
            return (
                <div className="min-h-[400px] flex items-center justify-center">
                    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-8 max-w-2xl mx-auto text-center">
                        <div className="text-red-400 mb-4">
                            <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                            </svg>
                        </div>

                        <h2 className="text-xl font-semibold text-white mb-2">
                            Something went wrong with reports
                        </h2>

                        <p className="text-gray-400 mb-6">
                            We encountered an unexpected error while loading your reports.
                            This might be a temporary issue.
                        </p>

                        <div className="space-y-3">
                            <button
                                onClick={this.handleRetry}
                                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-colors"
                            >
                                Try Again
                            </button>

                            <button
                                onClick={this.handleReload}
                                className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg text-white font-medium transition-colors"
                            >
                                Reload Page
                            </button>
                        </div>

                        {/* Error details for development */}
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="mt-6 text-left">
                                <summary className="cursor-pointer text-red-400 font-medium mb-2">
                                    Error Details (Development)
                                </summary>
                                <div className="bg-black/40 rounded-lg p-4 text-xs">
                                    <div className="text-red-300 font-mono">
                                        <strong>Error:</strong> {this.state.error.message}
                                    </div>
                                    {this.state.error.stack && (
                                        <pre className="text-gray-400 mt-2 whitespace-pre-wrap text-xs overflow-x-auto">
                                            {this.state.error.stack}
                                        </pre>
                                    )}
                                    {this.state.errorInfo && (
                                        <div className="mt-2">
                                            <strong className="text-red-300">Component Stack:</strong>
                                            <pre className="text-gray-400 mt-1 whitespace-pre-wrap text-xs">
                                                {this.state.errorInfo.componentStack}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

/**
 * Higher-order component for wrapping components with error boundary
 */
export function withHistoricalReportsErrorBoundary<P extends object>(
    WrappedComponent: React.ComponentType<P>,
    fallback?: ReactNode
) {
    const ComponentWithErrorBoundary = (props: P) => (
        <HistoricalReportsErrorBoundary fallback={fallback}>
            <WrappedComponent {...props} />
        </HistoricalReportsErrorBoundary>
    );

    ComponentWithErrorBoundary.displayName = `withHistoricalReportsErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;

    return ComponentWithErrorBoundary;
}